import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlusCircle, FiEdit, FiTrash2, FiRefreshCcw } from 'react-icons/fi';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import purchaseOrderService from '../services/purchaseOrderService';
import supplierService from '../services/supplierService';
import PurchaseOrderForm from '../components/purchaseOrders/PurchaseOrderForm';
import { formatInStoreTimezone } from '../utils/dateUtils';
import useStoreTimezone from '../hooks/useStoreTimezone';

const PURCHASE_ORDER_PERMISSIONS = {
  READ: 'purchase_orders:read',
  CREATE: 'purchase_orders:create',
  UPDATE: 'purchase_orders:update',
  SUBMIT: 'purchase_orders:submit',
  APPROVE: 'purchase_orders:approve',
  DELETE: 'purchase_orders:delete',
};

const statusBadges = {
  Draft: 'bg-gray-100 text-gray-700',
  Submitted: 'bg-blue-100 text-blue-700',
  Approved: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-700',
};

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const formatApproverName = (approvedBy) => {
  if (!approvedBy) return '—';
  if (typeof approvedBy === 'string') return approvedBy;
  return approvedBy.name || approvedBy.userId || approvedBy.email || '—';
};

const getSubmitBlockReason = (order) => {
  if (!order.lines?.length) return 'Add at least one line item before submitting.';
  if (!order.expectedDate) return 'Set a due date before submitting.';
  return '';
};

const PurchaseOrdersPage = () => {
  const { activeStore } = useStore();
  const { user } = useAuth();
  const timezone = useStoreTimezone();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [formReadOnly, setFormReadOnly] = useState(false);
  const [formDataLoading, setFormDataLoading] = useState(false);
  const [formLoadTarget, setFormLoadTarget] = useState(null);
  const [formLoadTitle, setFormLoadTitle] = useState('Create Purchase Order');
  const [actionTarget, setActionTarget] = useState('');
  const [savePending, setSavePending] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [invoiceStartDate, setInvoiceStartDate] = useState('');
  const [invoiceEndDate, setInvoiceEndDate] = useState('');
  const [totalsSummary, setTotalsSummary] = useState({ invoiceTotal: 0, taxTotal: 0 });
  const canRead = user?.permissions?.includes(PURCHASE_ORDER_PERMISSIONS.READ);
  const canCreate = user?.permissions?.includes(PURCHASE_ORDER_PERMISSIONS.CREATE);
  const canUpdate = user?.permissions?.includes(PURCHASE_ORDER_PERMISSIONS.UPDATE);
  const canSubmit = user?.permissions?.includes(PURCHASE_ORDER_PERMISSIONS.SUBMIT);
  const canApprove = user?.permissions?.includes(PURCHASE_ORDER_PERMISSIONS.APPROVE);
  const canDelete = user?.permissions?.includes(PURCHASE_ORDER_PERMISSIONS.DELETE);
  const isApproverQueueOnly =
    canApprove && !canCreate && !canUpdate && !canSubmit && !canDelete;

  const activeStoreId = activeStore?._id;

  useEffect(() => {
    if (isApproverQueueOnly) {
      setStatusFilter('Submitted');
    }
  }, [isApproverQueueOnly]);

  const loadSuppliers = useCallback(async () => {
    try {
      const supplierRes = await supplierService.fetchSuppliers({ limit: 200, compact: true });
      setSuppliers(supplierRes.data || []);
    } catch (err) {
      // non-blocking
    }
  }, []);

  useEffect(() => {
    if (canRead) {
      loadSuppliers();
    }
  }, [loadSuppliers, canRead]);

  const loadPurchaseOrders = useCallback(
    async (page = 1) => {
      if (!activeStoreId || !canRead) {
        return;
      }
      setLoading(true);
      setError('');
      try {
        const response = await purchaseOrderService.fetchPurchaseOrders({
          storeId: activeStoreId,
          status: (isApproverQueueOnly ? 'Submitted' : statusFilter) || undefined,
          supplierId: supplierFilter || undefined,
          invoiceStartDate: invoiceStartDate || undefined,
          invoiceEndDate: invoiceEndDate || undefined,
          page,
        });
        setOrders(response.data || []);
        setPagination(
          response.pagination || {
            page,
            pages: 1,
            total: response.data?.length || 0,
          }
        );
        setTotalsSummary(response.summary || { invoiceTotal: 0, taxTotal: 0 });
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to fetch purchase orders.');
      } finally {
        setLoading(false);
      }
    },
    [activeStoreId, statusFilter, supplierFilter, invoiceStartDate, invoiceEndDate, canRead, isApproverQueueOnly]
  );

  useEffect(() => {
    if (!activeStoreId) return;
    loadPurchaseOrders(1);
  }, [activeStoreId, statusFilter, supplierFilter, loadPurchaseOrders]);

  const handleOpenModal = async (order = null, { readOnly = false } = {}) => {
    const isReadOnlyView = Boolean(order?._id && readOnly);
    if (order && !isReadOnlyView && !canUpdate) {
      setError('You do not have permission to update purchase orders.');
      return;
    }
    if (!order && !canCreate) {
      setError('You do not have permission to create purchase orders.');
      return;
    }
    setFormDataLoading(true);
    setFormLoadTarget(order?._id || 'new');
    setFormLoadTitle(
      order
        ? `${isReadOnlyView ? 'View' : 'Edit'} Purchase Order ${order.poNumber || ''}`.trim()
        : 'Create Purchase Order'
    );
    setModalError('');
    setSelectedOrder(null);
    setFormReadOnly(isReadOnlyView);
    setModalOpen(true);
    try {
      if (!order?._id) {
        setSelectedOrder(null);
        setFormReadOnly(false);
        setFormDataLoading(false);
        return;
      }

      const detailed = await purchaseOrderService.getPurchaseOrder(order._id);
      setSelectedOrder(detailed);
      setFormReadOnly(readOnly || detailed.status !== 'Draft');
    } catch (err) {
      setModalOpen(false);
      setError(err.response?.data?.message || 'Unable to load purchase order details.');
      return;
    } finally {
      setFormDataLoading(false);
      setFormLoadTarget(null);
    }
  };

  const handleSaveOrder = async (form) => {
    if (selectedOrder?._id && !canUpdate) {
      setModalError('You do not have permission to update purchase orders.');
      return;
    }
    if (!selectedOrder?._id && !canCreate) {
      setModalError('You do not have permission to create purchase orders.');
      return;
    }
    if (savePending) return;
    setSavePending(true);
    setModalError('');
    setSuccessMessage('');
    try {
      if (selectedOrder?._id) {
        await purchaseOrderService.updatePurchaseOrder(selectedOrder._id, form);
      } else {
        await purchaseOrderService.createPurchaseOrder(form);
      }
      setModalOpen(false);
      setSelectedOrder(null);
      setFormReadOnly(false);
      setModalError('');
      setSuccessMessage('Purchase order saved successfully.');
      loadPurchaseOrders(pagination.page);
    } catch (err) {
      setModalError(err.response?.data?.message || 'Unable to save purchase order.');
    } finally {
      setSavePending(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canDelete) {
      setError('You do not have permission to delete purchase orders.');
      return;
    }
    if (!window.confirm('Delete this purchase order? Only drafts can be deleted.')) {
      return;
    }
    try {
      setActionTarget(`delete:${orderId}`);
      await purchaseOrderService.deletePurchaseOrder(orderId);
      loadPurchaseOrders(pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete purchase order.');
    } finally {
      setActionTarget('');
    }
  };

  const handleStatusChange = async (orderId, status) => {
    try {
      setActionTarget(`status:${orderId}:${status}`);
      setError('');
      setSuccessMessage('');
      await purchaseOrderService.updatePurchaseOrderStatus(orderId, status);
      setSuccessMessage(
        status === 'Submitted'
          ? 'Purchase order submitted successfully.'
          : status === 'Approved'
            ? 'Purchase order approved successfully.'
            : 'Purchase order returned to draft successfully.'
      );
      loadPurchaseOrders(pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status.');
    } finally {
      setActionTarget('');
    }
  };

  const supplierOptions = useMemo(() => suppliers, [suppliers]);
  const isNewOrderLoading = formDataLoading && formLoadTarget === 'new';
  const getOrderLoading = (orderId) => formDataLoading && formLoadTarget === orderId;
  const isActionLoading = (key) => actionTarget === key;

  if (!canRead) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-2">You do not have the required permissions to view purchase orders.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Purchase Orders</h1>
          <p className="text-gray-500">Track supplier orders and inbound shipments for this store.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => loadPurchaseOrders(pagination.page)}
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 inline-flex items-center"
          >
            <FiRefreshCcw className="mr-2" /> Refresh
          </button>
          {canCreate && (
            <button
              onClick={() => handleOpenModal(null)}
              disabled={formDataLoading}
              className="inline-flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FiPlusCircle className="mr-2" />
              {isNewOrderLoading ? 'Loading...' : 'New Purchase Order'}
            </button>
          )}
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          {isApproverQueueOnly ? (
            <div className="mt-1 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              Pending approval only
            </div>
          ) : (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
            >
              <option value="">All statuses</option>
              {Object.keys(statusBadges).map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Supplier</label>
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          >
            <option value="">All suppliers</option>
            {supplierOptions.map((supplier) => (
              <option key={supplier._id} value={supplier._id}>
                {supplier.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice Start Date</label>
          <input
            type="date"
            value={invoiceStartDate}
            onChange={(e) => setInvoiceStartDate(e.target.value)}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Invoice End Date</label>
          <input
            type="date"
            value={invoiceEndDate}
            onChange={(e) => setInvoiceEndDate(e.target.value)}
            className="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between mb-4 text-sm text-gray-600">
        <span>
          {pagination.total ? `${pagination.total} purchase orders` : 'No purchase orders yet'}
        </span>
        {(invoiceStartDate || invoiceEndDate) && (
          <button
            type="button"
            onClick={() => {
              setInvoiceStartDate('');
              setInvoiceEndDate('');
            }}
            className="text-primary hover:text-primary-dark"
          >
            Clear invoice date filter
          </button>
        )}
      </div>
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase text-gray-500">Total Invoice Amount</p>
          <p className="text-2xl font-semibold text-gray-900">
            {formatCurrency(totalsSummary.invoiceTotal)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase text-gray-500">Total GST</p>
          <p className="text-2xl font-semibold text-gray-900">
            {formatCurrency(totalsSummary.taxTotal)}
          </p>
        </div>
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      {successMessage && <p role="status" className="bg-green-100 text-green-800 p-3 rounded mb-4">{successMessage}</p>}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">PO #</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Supplier</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Invoice #</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Invoice Date</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Approval Date</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Approver's Name</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Due Date</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Order Value</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={10} className="px-6 py-6 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && orders.length === 0 && (
              <tr>
                <td colSpan={10} className="px-6 py-6 text-center text-gray-500">
                  No purchase orders found.
                </td>
              </tr>
            )}
            {orders.map((order) => (
              <tr key={order._id}>
                <td className="px-6 py-4">
                  <div className="text-sm font-semibold text-gray-900">{order.poNumber}</div>
                  <div className="text-xs text-gray-500">
                    Created {formatInStoreTimezone(order.createdAt, 'dd-MM-yyyy', timezone)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{order.supplier?.name}</div>
                  <div className="text-xs text-gray-500">{order.destinationStore?.name}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {order.invoiceNumber || '—'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {order.invoiceDate ? formatInStoreTimezone(order.invoiceDate, 'dd-MM-yyyy', timezone) : '—'}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadges[order.status] || 'bg-gray-100 text-gray-700'}`}>
                    {order.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {order.approvedDate ? formatInStoreTimezone(order.approvedDate, 'dd-MM-yyyy HH:mm', timezone) : '—'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {formatApproverName(order.approvedBy)}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {order.expectedDate ? formatInStoreTimezone(order.expectedDate, 'dd-MM-yyyy', timezone) : '—'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {formatCurrency(
                    order.amount != null && order.amount !== 0
                      ? Number(order.amount)
                      : Number(order.totals?.grandTotal || 0)
                  )}
                </td>
                <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                  {order.status === 'Draft' && canSubmit && (
                    <span className="inline-flex flex-col items-end">
                      <button
                        onClick={() => handleStatusChange(order._id, 'Submitted')}
                        disabled={Boolean(getSubmitBlockReason(order)) || isActionLoading(`status:${order._id}:Submitted`)}
                        title={getSubmitBlockReason(order)}
                        className="inline-flex items-center text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isActionLoading(`status:${order._id}:Submitted`) ? 'Submitting...' : 'Submit'}
                      </button>
                      {getSubmitBlockReason(order) && (
                        <span className="text-xs text-amber-700">{getSubmitBlockReason(order)}</span>
                      )}
                    </span>
                  )}
                  {order.status === 'Submitted' && canApprove && (
                    <button
                      onClick={() => handleStatusChange(order._id, 'Approved')}
                      disabled={isActionLoading(`status:${order._id}:Approved`)}
                      className="inline-flex items-center text-green-600 hover:text-green-900 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isActionLoading(`status:${order._id}:Approved`) ? 'Approving...' : 'Approve & Receive'}
                    </button>
                  )}
                  {order.status === 'Submitted' && (canSubmit || canApprove) && (
                    <button
                      onClick={() => handleStatusChange(order._id, 'Draft')}
                      disabled={isActionLoading(`status:${order._id}:Draft`)}
                      className="inline-flex items-center text-amber-700 hover:text-amber-900 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isActionLoading(`status:${order._id}:Draft`)
                        ? 'Updating...'
                        : canApprove
                          ? 'Reject to Draft'
                          : 'Move to Draft'}
                    </button>
                  )}
                  {order.status !== 'Draft' || !canUpdate ? (
                    <button
                      onClick={() => handleOpenModal(order, { readOnly: true })}
                      disabled={getOrderLoading(order._id)}
                      className="inline-flex items-center text-gray-600 hover:text-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {getOrderLoading(order._id) ? 'Loading...' : 'View'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleOpenModal(order)}
                      disabled={getOrderLoading(order._id)}
                      className="inline-flex items-center text-indigo-600 hover:text-indigo-900 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <FiEdit className="mr-1" /> {getOrderLoading(order._id) ? 'Loading...' : 'Edit'}
                    </button>
                  )}
                  {order.status === 'Draft' && canDelete && (
                    <button
                      onClick={() => handleDeleteOrder(order._id)}
                      disabled={isActionLoading(`delete:${order._id}`)}
                      className="inline-flex items-center text-red-600 hover:text-red-900 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <FiTrash2 className="mr-1" /> {isActionLoading(`delete:${order._id}`) ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t text-sm text-gray-600">
            <span>
              Page {pagination.page} of {pagination.pages} ({pagination.total} orders)
            </span>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => loadPurchaseOrders(Math.max(1, pagination.page - 1))}
                disabled={pagination.page <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => loadPurchaseOrders(Math.min(pagination.pages, pagination.page + 1))}
                disabled={pagination.page >= pagination.pages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-[98vw] max-w-[96rem] p-4 md:p-6 max-h-[92vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {selectedOrder
                ? `${formReadOnly ? 'View' : 'Edit'} Purchase Order ${selectedOrder.poNumber}`
                : formLoadTitle}
            </h2>
            {modalError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{modalError}</p>}
            {formDataLoading ? (
              <div className="py-10">
                <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-gray-50 p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Loading purchase order form...</p>
                      <p className="mt-1 text-sm text-gray-500">
                        Preparing purchase order details.
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-200">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-300"
                      style={{ width: '35%' }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative">
                <PurchaseOrderForm
                  purchaseOrder={selectedOrder}
                  suppliers={supplierOptions}
                  products={[]}
                  defaultStoreId={activeStoreId}
                  defaultStoreName={activeStore?.name}
                  timezone={timezone}
                  onSave={handleSaveOrder}
                  saving={savePending}
                  readOnly={formReadOnly}
                  onCancel={() => {
                    if (savePending) return;
                    setModalOpen(false);
                    setSelectedOrder(null);
                    setFormReadOnly(false);
                    setModalError('');
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
